import { useCallback, useEffect, useRef, useState } from "react";
import { parseTimecode } from "./utils.js";

/**
 * Следит за временем воспроизведения и «выстреливает» комментарии, чей таймкод
 * только что прошли. Возвращает список плашек для CommentToast.
 *
 *   const { toasts, dismiss, hold, release } = useCommentToasts({
 *     comments,        // массив комментариев 7cut (поля id, timecode, content, author, tags, image)
 *     currentTime,     // секунды, из onTimeUpdate плеера
 *     isPlaying,       // на паузе и при перемотке ничего не показываем
 *     holdMs: 4000,    // сколько висит точечный комментарий
 *     max: 2,          // максимум плашек одновременно (старые вытесняются)
 *     enabled: true,   // false — плашки выключены кнопкой, но onFire продолжает работать
 *     onFire: (comment) => {},  // проход таймкода (для автоскролла списка к комментарию)
 *   });
 *
 * Правила:
 *  - показываем только при движении вперёд с обычной скоростью; скачок > seekJumpSec
 *    (перемотка) плашек не создаёт;
 *  - диапазонный комментарий ("10 - 20") висит, пока время внутри диапазона, но не меньше holdMs;
 *  - один и тот же комментарий не показывается повторно, пока не пройдёт rearmSec
 *    (защита от дрожания времени вокруг таймкода);
 *  - hold(id)/release(id) — заморозить таймер, пока курсор на плашке.
 */
export function useCommentToasts({
  comments = [],
  currentTime = 0,
  isPlaying = false,
  holdMs = 4000,
  max = 2,
  seekJumpSec = 1.5,
  rearmSec = 3,
  leaveMs = 220,
  enabled = true,
  onFire,
}) {
  const onFireRef = useRef(onFire);
  onFireRef.current = onFire;
  const [toasts, setToasts] = useState([]);
  const lastTimeRef = useRef(null);
  const firedRef = useRef(new Map()); // id -> Date.now() последнего показа
  const heldRef = useRef(new Set());
  const timerRef = useRef(null);
  const toastsRef = useRef(toasts);
  toastsRef.current = toasts;

  // Разобранные таймкоды пересчитываем только при смене списка.
  const parsedRef = useRef([]);
  useEffect(() => {
    parsedRef.current = comments
      .map((c) => ({ comment: c, tc: parseTimecode(c.timecode) }))
      .filter((x) => x.tc)
      .sort((a, b) => a.tc.start - b.tc.start);
  }, [comments]);

  const schedulePrune = useCallback(() => {
    clearTimeout(timerRef.current);
    const list = toastsRef.current;
    if (!list.length) return;
    const now = Date.now();
    const next = Math.min(...list.map((t) => (t.leaving ? t.leaveAt : t.expiresAt)));
    timerRef.current = setTimeout(prune, Math.max(30, next - now));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const prune = useCallback(() => {
    const now = Date.now();
    const time = lastTimeRef.current ?? 0;
    setToasts((list) => {
      let changed = false;
      const out = [];
      for (const t of list) {
        if (t.leaving) {
          if (now >= t.leaveAt) { changed = true; continue; }
          out.push(t);
          continue;
        }
        const inRange = t.end != null && time >= t.start - 0.05 && time < t.end;
        const held = heldRef.current.has(t.id);
        if (!held && !inRange && now >= t.expiresAt) {
          out.push({ ...t, leaving: true, leaveAt: now + leaveMs });
          changed = true;
        } else {
          out.push(t);
        }
      }
      return changed ? out : list;
    });
    // Перепланировать после обновления состояния.
    setTimeout(schedulePrune, 0);
  }, [leaveMs, schedulePrune]);

  useEffect(() => {
    const prev = lastTimeRef.current;
    lastTimeRef.current = currentTime;
    if (!isPlaying) return;

    const jumped = prev == null || currentTime < prev || currentTime - prev > seekJumpSec;
    // После старта или перемотки смотрим только на узкое окно назад (0.35 с),
    // чтобы комментарий ровно на точке старта (например "0") тоже показался,
    // а всё, что перемотали, — нет.
    const from = jumped ? currentTime - 0.35 : prev;
    const now = Date.now();
    const hits = parsedRef.current.filter(({ comment, tc }) => {
      if (!(tc.start > from && tc.start <= currentTime)) return false;
      const firedAt = firedRef.current.get(comment.id);
      return firedAt == null || now - firedAt > holdMs + rearmSec * 1000;
    });
    if (!hits.length) return;
    hits.forEach(({ comment }) => firedRef.current.set(comment.id, now));
    // Событие прохода таймкода — независимо от того, показываем ли плашки.
    if (onFireRef.current) hits.forEach(({ comment }) => onFireRef.current(comment));
    if (!enabled) return;

    setToasts((list) => {
      const alive = list.filter((t) => !t.leaving && !hits.some((h) => h.comment.id === t.id));
      const fresh = hits.map(({ comment, tc }) => ({
        id: comment.id,
        comment,
        start: tc.start,
        end: tc.end,
        firedAt: now,
        expiresAt: now + holdMs,
        leaving: false,
      }));
      const merged = [...alive, ...fresh];
      // Самые новые внизу; лишние сверху уходят с анимацией.
      const keep = merged.slice(-max);
      const drop = merged
        .slice(0, Math.max(0, merged.length - max))
        .map((t) => ({ ...t, leaving: true, leaveAt: now + leaveMs }));
      return [...drop, ...keep];
    });
    setTimeout(schedulePrune, 0);
  }, [currentTime, isPlaying, holdMs, max, seekJumpSec, rearmSec, leaveMs, enabled, schedulePrune]);

  // Выключили плашки кнопкой — убираем те, что уже висят.
  useEffect(() => {
    if (enabled) return;
    const now = Date.now();
    setToasts((list) => list.map((t) => (t.leaving ? t : { ...t, leaving: true, leaveAt: now + leaveMs })));
    setTimeout(schedulePrune, 0);
  }, [enabled, leaveMs, schedulePrune]);

  useEffect(() => () => clearTimeout(timerRef.current), []);

  const dismiss = useCallback((id) => {
    const now = Date.now();
    setToasts((list) => list.map((t) => (t.id === id && !t.leaving ? { ...t, leaving: true, leaveAt: now + leaveMs } : t)));
    setTimeout(schedulePrune, 0);
  }, [leaveMs, schedulePrune]);

  const hold = useCallback((id) => { heldRef.current.add(id); }, []);
  const release = useCallback((id) => {
    heldRef.current.delete(id);
    const now = Date.now();
    // После ухода курсора даём ещё секунду.
    setToasts((list) => list.map((t) => (t.id === id && !t.leaving ? { ...t, expiresAt: Math.max(t.expiresAt, now + 1000) } : t)));
    setTimeout(schedulePrune, 0);
  }, [schedulePrune]);

  return { toasts, dismiss, hold, release };
}
